interface QuestionPresentation {
  [key: number]: PresentationContent;
}

interface PresentationContent {
  groupName?: string;
  labelForSelect?: string;
  questionTooltip?: QuestionTooltip;
  subtitle?: string;
  pageTitle: string;
  component: Component;
}

interface QuestionTooltip {
  title: string;
  content: string[];
}

interface Component {
  type: ComponentType;
  content?:
    | RadioAndSelectContent
    | RadioAndCheckboxContent
    | RadioAndTooltipContent;
}

interface RadioAndSelectContent {
  showSelectLabel: string;
  showSelectValue: number;
}

interface RadioAndCheckboxContent {
  showCheckboxLabel: string;
  showCheckboxValue: number;
  labelForCheckbox: string;
}

export interface TooltipCompactView {
  tooltipWidth: number;
  showTooltip: boolean;
}

export interface RadioAndTooltipContent {
  compactView: TooltipCompactView;
  replaceManufacturerName?: boolean;
  showAsButtons: boolean;
}

interface QuestionRoutingLabels {
  [key: number]: string;
}

export enum ComponentType {
  RADIOANDSELECT = 1,
  RADIOWITHTOOTIP = 2,
  RADIOANDCHECKBOX = 3
}
export const yesMaskValue = -1;
export const defaultAnswerState = [-2];
export enum QuestionIds {
  WHEELS = 18,
  ASSUMPTION = 1,
  SERVICEHISTORY = 2,
  TAXI = 11,
  EXTERIORBODYWORK = 14,
  TYRES = 15,
  INTERIOR = 16,
  MECHANICSELECTRICS = 17,
  KEYS = 6,
  MODIFICATIONS = 24,
  MISSINGITEMS = 22,
  GLASS = 23,
  INSURANCELOSS = 5
}
export const FactualQuestionsOrder: number[] = [
  QuestionIds.KEYS,
  QuestionIds.SERVICEHISTORY,
  QuestionIds.TAXI
];
export const ConditionQuestionsOrder: number[] = [
  QuestionIds.EXTERIORBODYWORK,
  QuestionIds.TYRES,
  QuestionIds.WHEELS,
  QuestionIds.INTERIOR,
  QuestionIds.MECHANICSELECTRICS
];
export const questionRoutingLabels: QuestionRoutingLabels = {
  [QuestionIds.WHEELS]: "wheels",
  [QuestionIds.SERVICEHISTORY]: "service-history",
  [QuestionIds.TAXI]: "vehicle-usage",
  [QuestionIds.EXTERIORBODYWORK]: "bodywork",
  [QuestionIds.TYRES]: "tyres",
  [QuestionIds.INTERIOR]: "interior",
  [QuestionIds.MECHANICSELECTRICS]: "mechanics",
  [QuestionIds.KEYS]: "keys",
  [QuestionIds.MODIFICATIONS]: "modifications"
};

export const questionPresentationContent: QuestionPresentation = {
  [QuestionIds.WHEELS]: {
    pageTitle: "Trims/ alloy wheels",
    groupName: "wheels",
    questionTooltip: {
      title: "How do I know if my wheels need to be repaired?",
      content: [
        "Look for scratches, rust, dents, corrosion and bubbling.",
        "A constant deflation of tyres or a rattling sound when driving could also be due to wheel damage."
      ]
    },
    labelForSelect: "Select number of wheels",
    component: {
      type: ComponentType.RADIOANDSELECT,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.SERVICEHISTORY]: {
    pageTitle: "What service history does your vehicle have?",
    groupName: "service",
    subtitle: "Find this information in your printed service book or digital service log.",
    component: {
      type: ComponentType.RADIOWITHTOOTIP,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1,
        showAsButtons: true,
        replaceManufacturerName: true,
        compactView: {
          showTooltip: true,
          tooltipWidth: 448,
        }
      }
    }
  },
  [QuestionIds.TAXI]: {
    pageTitle: "Vehicle usage",
    groupName: "taxi",
    component: {
      type: ComponentType.RADIOWITHTOOTIP,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.EXTERIORBODYWORK]: {
    pageTitle: "Exterior bodywork",
    groupName: "bodywork",
    questionTooltip: {
      title:
        "What to look for when checking your cars bodywork for imperfections?",
      content: [
        "Signs of damage to look out for are scratches or chips that go through the paint, rust and dents."
      ]
    },
    labelForSelect: "Select number of panels",
    component: {
      type: ComponentType.RADIOANDSELECT,
      content: {
        showSelectLabel: "There are imperfections on the exterior bodywork ",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.TYRES]: {
    pageTitle: "Tyres",
    groupName: "tyres",
    questionTooltip: {
      title: "How do I know if my tyre needs to be replaced?",
      content: [
        "We will need to replace your tyre(s) if the tread is below 3mm, if you can see damage to your tyres such as a lump, bulge, or exposed cords."
      ]
    },
    component: {
      type: ComponentType.RADIOANDSELECT,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.INTERIOR]: {
    pageTitle: "Interior",
    groupName: "interior",
    component: {
      type: ComponentType.RADIOWITHTOOTIP,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1,
        showAsButtons: true,
        compactView: {
          showTooltip: true,
          tooltipWidth: 512,
        }
      }
    }
  },
  [QuestionIds.KEYS]: {
    pageTitle: "Number of keys",
    groupName: "keys",
    component: {
      type: ComponentType.RADIOWITHTOOTIP
    }
  },
  [QuestionIds.MECHANICSELECTRICS]: {
    pageTitle: "Mechanics & electrics",
    groupName: "electrics",
    questionTooltip: {
      title: "How do I know if there are any problems?",
      content: [
        "If your car has been making unusual noises or you see warning lights on your dashboard, this may indicate an electrical or mechanical problem."
      ]
    },
    component: {
      type: ComponentType.RADIOANDSELECT,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.MODIFICATIONS]: {
    pageTitle: "Modifications",
    groupName: "modifications",
    component: {
      type: ComponentType.RADIOWITHTOOTIP,
      content: {
        showSelectLabel: "Yes",
        showSelectValue: -1
      }
    }
  },
  [QuestionIds.MISSINGITEMS]: {
    pageTitle: "Missing items",
    groupName: "items",
    component: {
      type: ComponentType.RADIOANDCHECKBOX,
      content: {
        showCheckboxLabel: "Yes",
        showCheckboxValue: -1,
        labelForCheckbox: "Select the items missing from your car:"
      }
    }
  },
  [QuestionIds.GLASS]: {
    pageTitle: "Glass",
    groupName: "glass",
    component: {
      type: ComponentType.RADIOANDCHECKBOX,
      content: {
        showCheckboxLabel: "Yes",
        showCheckboxValue: -1,
        labelForCheckbox: "Select the windows with damage:"
      }
    }
  }
};
